import { ColumnState } from "ag-grid-community";
import { create } from "zustand";
import { HRM3130_R01TaxTableDto, HRM3130_R01TaxDto, defaultHRM3130_R01TaxTableDto } from "./Dto_HRM3130_R01";
/** 목록 연도와 미리보기 입력 조건이다. */
export interface HRM3130_R01Filter {
    applyYear: string; // 적용년도
    taxableAmt: string; // 콤마 없는 급여 입력
    familyCount: string; // 가족수 선택값
}
export const defaultHRM3130_R01Filter: HRM3130_R01Filter = { applyYear: String(new Date().getFullYear()), taxableAmt: '', familyCount: '1' }; // 올해·1명 기본값
/** 화면 상태와 상태 변경 계약이다. */
export interface Store_HRM3130_R01 {
    HRM3130_R01Filter: HRM3130_R01Filter; // HRM3130_R01Filter 상태
    setHRM3130_R01Filter: (value: Partial<HRM3130_R01Filter>) => void; // HRM3130_R01Filter 갱신
    HRM3130_R01PastFilter: HRM3130_R01Filter; // HRM3130_R01PastFilter 상태
    setHRM3130_R01PastFilter: (value: Partial<HRM3130_R01Filter>) => void; // HRM3130_R01PastFilter 갱신
    HRM3130_R01List: HRM3130_R01TaxTableDto[]; // HRM3130_R01List 상태
    setHRM3130_R01List: (value: HRM3130_R01TaxTableDto[]) => void; // HRM3130_R01List 갱신
    originalHRM3130_R01List: HRM3130_R01TaxTableDto[]; // originalHRM3130_R01List 상태
    setOriginalHRM3130_R01List: (value: HRM3130_R01TaxTableDto[]) => void; // 조회 원본 갱신
    selectedHRM3130_R01List: string[]; // selectedHRM3130_R01List 상태
    setSelectedHRM3130_R01List: (value: string[]) => void; // 선택 목록 갱신
    deletedHRM3130_R01List: string[]; // deletedHRM3130_R01List 상태
    setDeletedHRM3130_R01List: (value: string[]) => void; // 삭제 목록 갱신
    HRM3130_R01IncomeTax: HRM3130_R01TaxDto | undefined; // HRM3130_R01IncomeTax 상태
    setHRM3130_R01IncomeTax: (value: HRM3130_R01TaxDto | undefined) => void; // HRM3130_R01IncomeTax 갱신
    HRM3130_R01YearList: string[]; // HRM3130_R01YearList 상태
    setHRM3130_R01YearList: (value: string[]) => void; // HRM3130_R01YearList 갱신
    columnState: ColumnState[] | null; // columnState 상태
    setColumnState: (value: ColumnState[] | null) => void; // 컬럼 상태 갱신
    scrollTop: number | null; // scrollTop 상태
    setScrollTop: (value: number | null) => void; // 스크롤 위치 갱신
    insertHRM3130_R01List: (rows: HRM3130_R01TaxTableDto[], index?: number) => void; // 기준 행 뒤에 삽입
    removeHRM3130_R01List: (id: number) => void; // 신규 행 제거
    updateHRM3130_R01List: (id: number, row: HRM3130_R01TaxTableDto) => void; // 셀 편집 반영
    addSelectedHRM3130_R01List: (ids: string[]) => void; // 단일 선택 교체
    removeSelectedHRM3130_R01List: (ids?: string[]) => void; // 선택 해제
    initSelectedHRM3130_R01List: () => void; // 전체 선택 해제
    allSelectedHRM3130_R01List: () => void; // 전체 행 선택
    addDeletedHRM3130_R01List: (ids: string[]) => void; // 삭제 표시 추가
    removeDeletedHRM3130_R01List: (ids?: string[]) => void; // 삭제 표시 해제
    initData: () => void; // 전체 상태 초기화
}
/** 조회 행을 화면 기본값과 인덱스로 보완해 반환한다. */
const buildGridList = (rows: HRM3130_R01TaxTableDto[]) => rows.map((row, i) => ({
    ...defaultHRM3130_R01TaxTableDto, ...row,
    valueIndex: row.valueIndex > 0 ? row.valueIndex : i + 1,
    orderIndex: row.orderIndex > 0 ? row.orderIndex : i + 1,
    orderIndexSeq: row.orderIndexSeq || 0,
}));
/** 최초 진입과 초기화에 사용하는 화면 상태를 반환한다. */
const initialState = () => ({
    HRM3130_R01Filter: { ...defaultHRM3130_R01Filter },
    // 조회 전 저장·업로드를 막기 위해 직전 조회 연도는 비워 둔다.
    HRM3130_R01PastFilter: { ...defaultHRM3130_R01Filter, applyYear: '' },
    HRM3130_R01List: [], originalHRM3130_R01List: [],
    selectedHRM3130_R01List: [], deletedHRM3130_R01List: [],
    HRM3130_R01IncomeTax: undefined, HRM3130_R01YearList: [],
    columnState: null, scrollTop: null,
});
/** API 호출 없이 화면 상태만 관리하는 Zustand 훅이다. */
export const useHRM3130_R01Store = create<Store_HRM3130_R01>((set) => ({
    ...initialState(),
    /** HRM3130_R01Filter를 전달값으로 갱신한다. 필터는 부분 병합한다. */
    setHRM3130_R01Filter: value => set(state => ({ HRM3130_R01Filter: { ...state.HRM3130_R01Filter, ...value } })),
    /** HRM3130_R01PastFilter를 전달값으로 갱신한다. 필터는 부분 병합한다. */
    setHRM3130_R01PastFilter: value => set(state => ({ HRM3130_R01PastFilter: { ...state.HRM3130_R01PastFilter, ...value } })),
    /** 전달 행에 그리드 기본값과 식별 인덱스를 채워 목록으로 저장한다. */
    setHRM3130_R01List: value => set({ HRM3130_R01List: buildGridList(value) }),
    /** 조회 원본에 동일한 식별 인덱스를 부여해 비교 기준으로 저장한다. */
    setOriginalHRM3130_R01List: value => set({ originalHRM3130_R01List: buildGridList(value) }),
    /** 선택 식별값을 문자열로 맞춰 전체 교체한다. */
    setSelectedHRM3130_R01List: value => set({ selectedHRM3130_R01List: value.map(String) }),
    /** 삭제 식별값을 문자열로 맞춰 전체 교체한다. */
    setDeletedHRM3130_R01List: value => set({ deletedHRM3130_R01List: value.map(String) }),
    /** 서버 미리보기 결과를 저장하거나 undefined로 지운다. */
    setHRM3130_R01IncomeTax: value => set({ HRM3130_R01IncomeTax: value }),
    /** 서버에서 읽은 등록 연도 목록으로 교체한다. */
    setHRM3130_R01YearList: value => set({ HRM3130_R01YearList: value }),
    /** 컬럼 배치·너비를 갱신한다. null이면 기본 배치를 사용한다. */
    setColumnState: value => set({ columnState: value }),
    /** 그리드 스크롤 위치를 저장한다. null이면 복원 위치를 비운다. */
    setScrollTop: value => set({ scrollTop: value }),
    /** HRM2130 방식으로 고유 식별값과 기준 행 정렬값을 부여해 삽입한다. */
    insertHRM3130_R01List: (rows, index) => set(state => {
        const list = [...state.HRM3130_R01List]; // 현재 목록 복사
        const target = list.find(row => row.valueIndex === index); // 기준 행
        const max = Math.max(0, ...list.map(row => row.valueIndex)); // 식별값 최댓값
        const added = rows.map((row, i) => ({ ...row, valueIndex: max + i + 1, orderIndex: target?.orderIndex ?? list.length + 1, orderIndexSeq: (target?.orderIndexSeq ?? 0) + 1 })); // 신규 식별값 부여
        list.splice(index === undefined ? list.length : Math.max(0, list.findIndex(row => row.valueIndex === index) + 1), 0, ...added);
        return { HRM3130_R01List: list };
    }),
    /** 신규 행과 연결된 선택·삭제 식별값을 제거한다. */
    removeHRM3130_R01List: id => set(state => ({
        HRM3130_R01List: state.HRM3130_R01List.filter(row => row.valueIndex !== id),
        selectedHRM3130_R01List: state.selectedHRM3130_R01List.filter(value => value !== String(id)),
        deletedHRM3130_R01List: state.deletedHRM3130_R01List.filter(value => value !== String(id)),
    })),
    /** 지정 식별값의 편집 결과만 교체한다. */
    updateHRM3130_R01List: (id, row) => set(state => ({ HRM3130_R01List: state.HRM3130_R01List.map(item => item.valueIndex === id ? row : item) })),
    /** 공통 훅의 숫자 식별값을 문자열로 바꾸고 선택 목록을 교체한다. */
    addSelectedHRM3130_R01List: ids => set({ selectedHRM3130_R01List: ids.map(String) }),
    /** 지정 선택만 해제하거나 전체 선택을 해제한다. */
    removeSelectedHRM3130_R01List: ids => set(state => ({ selectedHRM3130_R01List: ids ? state.selectedHRM3130_R01List.filter(id => !ids.map(String).includes(id)) : [] })),
    /** 전체 선택을 해제한다. */
    initSelectedHRM3130_R01List: () => set({ selectedHRM3130_R01List: [] }),
    /** 현재 실제 데이터 행을 전부 선택한다. */
    allSelectedHRM3130_R01List: () => set(state => ({ selectedHRM3130_R01List: state.HRM3130_R01List.map(row => String(row.valueIndex)) })),
    /** 문자열 식별값으로 삭제 표시를 중복 없이 추가한다. */
    addDeletedHRM3130_R01List: ids => set(state => ({ deletedHRM3130_R01List: Array.from(new Set([...state.deletedHRM3130_R01List, ...ids.map(String)])) })),
    /** 지정 삭제 표시만 해제하거나 전체 삭제 표시를 해제한다. */
    removeDeletedHRM3130_R01List: ids => set(state => ({ deletedHRM3130_R01List: ids ? state.deletedHRM3130_R01List.filter(id => !ids.map(String).includes(id)) : [] })),
    /** 필터·목록·선택·미리보기·그리드 상태 전부를 초기화한다. */
    initData: () => set(initialState()),
}));
